import api from '@/lib/api';
import type {
  CreatePreboardingFormInput,
  PreboardingForm,
  UpdatePreboardingFormInput,
} from '@hygge/shared';
import { CACHE_TIME } from '@hygge/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

export const preboardingFormsKeys = {
  all: ['preboarding-forms'] as const,
  detail: (id: string) => [...preboardingFormsKeys.all, id] as const,
};

async function listForms(): Promise<PreboardingForm[]> {
  const { data } = await api.get<PreboardingForm[]>('/preboarding-forms');
  return data;
}

async function getForm(id: string): Promise<PreboardingForm> {
  const { data } = await api.get<PreboardingForm>(`/preboarding-forms/${id}`);
  return data;
}

async function createForm(input: CreatePreboardingFormInput): Promise<PreboardingForm> {
  const { data } = await api.post<PreboardingForm>('/preboarding-forms', input);
  return data;
}

async function updateForm(
  id: string,
  input: UpdatePreboardingFormInput,
): Promise<PreboardingForm> {
  const { data } = await api.patch<PreboardingForm>(`/preboarding-forms/${id}`, input);
  return data;
}

async function deleteForm(id: string): Promise<void> {
  await api.delete(`/preboarding-forms/${id}`);
}

async function uploadCover(id: string, file: Blob): Promise<PreboardingForm> {
  const formData = new FormData();
  formData.append('file', file, 'cover');
  const { data } = await api.post<PreboardingForm>(`/preboarding-forms/${id}/cover`, formData);
  return data;
}

async function deleteCover(id: string): Promise<PreboardingForm> {
  const { data } = await api.delete<PreboardingForm>(`/preboarding-forms/${id}/cover`);
  return data;
}

export function usePreboardingForms() {
  return useQuery({
    queryKey: preboardingFormsKeys.all,
    queryFn: listForms,
    staleTime: CACHE_TIME.DEFAULT,
  });
}

export function usePreboardingForm(id: string) {
  return useQuery({
    queryKey: preboardingFormsKeys.detail(id),
    queryFn: () => getForm(id),
    staleTime: CACHE_TIME.DEFAULT,
    enabled: !!id,
  });
}

export function useCreatePreboardingForm() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createForm,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: preboardingFormsKeys.all });
    },
  });
}

// `suppressGlobalToast` is an opt-in: the wizard steps' autosave (welcome,
// completion, setup-emails) already surface a "Failed to save" status pill
// and pass true; preboarding-form-wizard.tsx's publish/unpublish toggle has
// no handling of its own and omits it, so the global fallback covers that.
export function useUpdatePreboardingForm(opts?: { suppressGlobalToast?: boolean }) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdatePreboardingFormInput }) =>
      updateForm(id, input),
    meta: { suppressGlobalToast: opts?.suppressGlobalToast ?? false },
    onSuccess: (form) => {
      queryClient.setQueryData(preboardingFormsKeys.detail(form.id), form);
      queryClient.invalidateQueries({ queryKey: preboardingFormsKeys.all });
    },
  });
}

export function useDeletePreboardingForm() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteForm,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: preboardingFormsKeys.all });
    },
  });
}

function cacheUpdatedForm(queryClient: ReturnType<typeof useQueryClient>, form: PreboardingForm) {
  queryClient.setQueryData(preboardingFormsKeys.detail(form.id), form);
  queryClient.invalidateQueries({ queryKey: preboardingFormsKeys.all });
}

export function useUploadPreboardingCover() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, file }: { id: string; file: Blob }) => uploadCover(id, file),
    onSuccess: (form) => cacheUpdatedForm(queryClient, form),
  });
}

export function useDeletePreboardingCover() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteCover(id),
    onSuccess: (form) => cacheUpdatedForm(queryClient, form),
  });
}
